import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import {
  Controller,
  FormProvider,
  useFormContext,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import { useCraneT } from "../lib/i18n";
import { cn } from "../lib/utils";
import { Label } from "./label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue | null>(null);

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }
  if (!itemContext) {
    throw new Error("useFormField should be used within <FormItem>");
  }

  const fieldState = getFieldState(fieldContext.name, formState);
  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

const FormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const id = React.useId();

    return (
      <FormItemContext.Provider value={{ id }}>
        <div ref={ref} className={cn("space-y-2", className)} {...props} />
      </FormItemContext.Provider>
    );
  },
);
FormItem.displayName = "FormItem";

/** Etykieta pozostaje neutralna także w stanie błędu — sygnał niosą ramka pola
 *  (`aria-invalid`) i komunikat pod nim. Czerwony label byłby trzecim powtórzeniem
 *  tej samej informacji i przy formularzu z kilkunastoma polami zamienia widok w ścianę czerwieni. */
const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { formItemId } = useFormField();

  return <Label ref={ref} className={className} htmlFor={formItemId} {...props} />;
});
FormLabel.displayName = "FormLabel";

const FormControl = React.forwardRef<React.ElementRef<typeof Slot>, React.ComponentPropsWithoutRef<typeof Slot>>(
  ({ ...props }, ref) => {
    const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

    return (
      <Slot
        ref={ref}
        id={formItemId}
        aria-describedby={!error ? formDescriptionId : `${formDescriptionId} ${formMessageId}`}
        aria-invalid={!!error}
        {...props}
      />
    );
  },
);
FormControl.displayName = "FormControl";

const FormDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => {
    const { formDescriptionId } = useFormField();

    return (
      <p ref={ref} id={formDescriptionId} className={cn("text-sm text-crane-muted-foreground", className)} {...props} />
    );
  },
);
FormDescription.displayName = "FormDescription";

const FormMessage = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, children, ...props }, ref) => {
    const { error, formMessageId } = useFormField();
    const t = useCraneT();
    /** Komunikaty zod trzymamy jako KLUCZE i18n (schematy to moduły bez hooków).
     *  `defaultValue` zachowuje zastane zachowanie: komunikat, który nie jest kluczem,
     *  renderuje się dosłownie — bez zmian w formularzach spoza modułu administracji. */
    const rawMessage = error ? String(error.message ?? "") : "";
    const body = error ? (rawMessage ? t(rawMessage, { defaultValue: rawMessage }) : "") : children;

    if (!body) {
      return null;
    }

    return (
      <p
        ref={ref}
        id={formMessageId}
        className={cn("text-sm font-medium text-crane-destructive", className)}
        {...props}
      >
        {body}
      </p>
    );
  },
);
FormMessage.displayName = "FormMessage";

/** Treść błędu w dymku zamiast pod polem: komunikat nie wchodzi w układ, więc wysokość
 *  wiersza tabeli nie skacze po walidacji i kontrolki sąsiednich kolumn zostają wyrównane.
 *  Dymek jest portalowany, bo kontener tabeli ma `overflow: auto` i przyciąłby element
 *  pozycjonowany absolutnie w ostatnim wierszu. Sygnałem stałym pozostaje czerwona ramka
 *  kontrolki (`aria-invalid`); pełną listę błędów widok pokazuje w powiadomieniu przy zapisie. */
const FormCellContent = ({ children }: { children?: React.ReactNode }) => {
  const { error, formMessageId } = useFormField();
  const t = useCraneT();
  const rawMessage = error ? String(error.message ?? "") : "";

  if (!rawMessage) {
    return <>{children}</>;
  }

  const message = t(rawMessage, { defaultValue: rawMessage });

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs border border-crane-destructive text-crane-destructive">
          {message}
        </TooltipContent>
      </Tooltip>
      {/* `aria-describedby` z `FormControl` wskazuje na ten element — dymek istnieje w DOM
          dopiero po otwarciu, więc czytnik ekranu potrzebuje stałej kopii komunikatu. */}
      <span id={formMessageId} className="sr-only">
        {message}
      </span>
    </TooltipProvider>
  );
};

/** `FormItem` dla pola w wierszu tabeli formularza — zamiast pary `FormItem` + `FormMessage`.
 *  Formularze jednokolumnowe zostają przy `FormItem`, gdzie komunikat pod polem niczego nie
 *  przesuwa. Dymek podpina się pod kontrolkę przez `asChild`, więc dziecko musi przekazywać
 *  propsy i `ref` dalej (`FormControl` to robi). */
const FormCell = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <FormItem ref={ref} className={cn("space-y-0", className)} {...props}>
      <FormCellContent>{children}</FormCellContent>
    </FormItem>
  ),
);
FormCell.displayName = "FormCell";

export {
  useFormField,
  Form,
  FormItem,
  FormCell,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
};
